import React, { useState } from "react";
import { STATUS, PRIORITY, initials, daysUntil, formatDate } from "../lib/utils";
import { AlertCircle, Clock } from "lucide-react";

const COLUMNS = ["pendiente", "en_revision", "en_proceso", "cumplido", "vencidas"];

const COLUMN_META = {
  ...STATUS,
  vencidas: { label: "Tareas vencidas", color: "#EE1C25", bg: "bg-red-100", text: "text-red-700" },
};

function isOverdue(t) {
  return t.status !== "cumplido" && t.due_date && daysUntil(t.due_date) < 0;
}

export default function KanbanBoard({ tasks, users, onCardClick, onStatusChange, canEdit }) {
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  // Columna "Tareas vencidas": tareas con fecha límite pasada sin cumplir + tareas detenidas
  const byStatus = { pendiente: [], en_proceso: [], en_revision: [], cumplido: [], vencidas: [] };
  tasks.forEach(t => {
    if (isOverdue(t) || t.status === "detenido") byStatus.vencidas.push(t);
    else (byStatus[t.status] || byStatus.pendiente).push(t);
  });

  const userMap = Object.fromEntries((users||[]).map(u => [u.id, u]));

  const onDragStart = (e, id) => {
    if (!canEdit) return;
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = (e, status) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragId) return;
    const task = tasks.find(t => t.id === dragId);
    if (task && task.status !== status) onStatusChange(task, status);
    setDragId(null);
  };

  return (
    <div className="overflow-x-auto pb-4">
      <div className="grid grid-cols-5 gap-4 min-w-[1100px]">
        {COLUMNS.map(status => {
          const meta = COLUMN_META[status];
          const items = byStatus[status];
          const droppable = canEdit && status !== "vencidas";
          return (
            <div
              key={status}
              onDragOver={(e) => { if (droppable) { e.preventDefault(); setDragOver(status); }}}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => droppable && onDrop(e, status)}
              className={`kanban-column ${status === "vencidas" ? "bg-red-50/50 border-red-200" : "bg-slate-50/60 border-slate-200"} border rounded-lg p-3 min-h-[400px] ${dragOver===status ? "drag-over" : ""}`}
              data-testid={`kanban-col-${status}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="status-dot" style={{ background: meta.color }} />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{meta.label}</span>
                </div>
                <span className="text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5 min-w-[26px] text-center">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map(t => (
                  <TaskCard key={t.id} task={t} user={userMap[t.assignee_id]} onClick={() => onCardClick(t)} onDragStart={(e) => onDragStart(e, t.id)} canDrag={canEdit} isDragging={dragId===t.id} showStatus={status === "vencidas"} />
                ))}
                {items.length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-md">
                    {status === "vencidas" ? "Sin tareas vencidas" : "Sin tareas"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TaskCard({ task, user, onClick, onDragStart, canDrag, isDragging, showStatus }) {
  const days = daysUntil(task.due_date);
  const overdue = task.status !== "cumplido" && days !== null && days < 0;
  const dueSoon = task.status !== "cumplido" && days !== null && days >= 0 && days <= 3;
  const p = PRIORITY[task.priority] || PRIORITY.media;
  const meta = STATUS[task.status];

  return (
    <div
      draggable={canDrag}
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={`kanban-task-card-${task.id}`}
      className={`kanban-card bg-white border border-slate-200 rounded-md p-3 cursor-pointer text-sm ${isDragging ? "dragging" : ""}`}
      style={{ borderLeft: `3px solid ${meta.color}` }}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="font-medium text-slate-900 leading-snug line-clamp-2">{task.title}</div>
        <div className="flex gap-1 shrink-0">
          {showStatus && (
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.bg} ${meta.text}`}>{meta.label}</span>
          )}
          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${p.bg} ${p.text}`}>{p.label}</span>
        </div>
      </div>
      {task.description && <p className="text-xs text-slate-500 line-clamp-2 mb-2">{task.description}</p>}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          {user ? (
            <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-[10px] font-semibold flex items-center justify-center" title={user.name}>{initials(user.name)}</div>
          ) : (
            <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-500 text-[10px] flex items-center justify-center">?</div>
          )}
          {task.tags?.slice(0,2).map(t => (
            <span key={t} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{t}</span>
          ))}
        </div>
        {task.due_date && (
          <div className={`flex items-center gap-1 text-[11px] ${overdue ? "text-crejpp font-semibold" : dueSoon ? "text-amber-600 font-semibold" : "text-slate-500"}`}>
            {overdue ? <AlertCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
            {formatDate(task.due_date)}
          </div>
        )}
      </div>
      {task.progress > 0 && task.progress < 100 && (
        <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-crejpp rounded-full" style={{ width: `${task.progress}%` }} />
        </div>
      )}
    </div>
  );
}
